import React, { useState, useRef } from 'react';

const ReelCreator = ({ currentUser, onClose, onPublish }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [caption, setCaption] = useState('');
  const [audioName, setAudioName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('video/')) return;
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    if (!audioName) setAudioName('Original Audio');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handlePublish = async () => {
    if (!videoFile && !videoPreview) return;
    setUploading(true);
    try {
      await onPublish({
        videoFile,
        videoPreview,
        caption,
        audioName: audioName || 'Original Audio',
      });
      onClose();
    } catch (err) {
      console.error('Reel publish error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-md" onClick={onClose} />

      {/* Modal */}
      <div
        className="fixed inset-0 z-[301] flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-full max-w-lg rounded-2xl overflow-hidden"
          style={{
            background: 'rgba(15,15,20,0.96)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <h2 className="text-white font-bold text-lg tracking-wide">New Reel</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <span className="material-symbols-outlined text-white/60">close</span>
            </button>
          </div>

          <div className="p-6 space-y-5">
            {/* Drop zone */}
            {!videoPreview ? (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className="relative rounded-xl cursor-pointer flex flex-col items-center justify-center gap-3 transition-all"
                style={{
                  height: '220px',
                  border: `2px dashed ${dragOver ? '#c9a96e' : 'rgba(255,255,255,0.15)'}`,
                  background: dragOver ? 'rgba(201,169,110,0.08)' : 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#c9a96e22,#e8c98a22)' }}
                >
                  <span
                    className="material-symbols-outlined text-[28px]"
                    style={{ color: '#c9a96e' }}
                  >
                    video_file
                  </span>
                </div>
                <div className="text-center">
                  <p className="text-white font-semibold text-sm">Drop a video or click to browse</p>
                  <p className="text-white/40 text-xs mt-1">MP4, MOV, WebM · Max 200MB</p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/*"
                  className="sr-only"
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
            ) : (
              <div className="relative rounded-xl overflow-hidden" style={{ height: '220px' }}>
                <video
                  src={videoPreview}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  autoPlay
                  playsInline
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <button
                  onClick={() => { setVideoFile(null); setVideoPreview(''); }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                >
                  <span className="material-symbols-outlined text-white text-[16px]">close</span>
                </button>
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-white text-[14px]">check_circle</span>
                  <span className="text-white text-xs font-semibold">{videoFile?.name || 'Video ready'}</span>
                </div>
              </div>
            )}

            {/* Caption */}
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest font-semibold mb-2 block">Caption</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write a caption…"
                rows={3}
                className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none resize-none transition-colors"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(201,169,110,0.4)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              />
            </div>

            {/* Audio name */}
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest font-semibold mb-2 block">Audio Name</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-[18px]">music_note</span>
                <input
                  type="text"
                  value={audioName}
                  onChange={(e) => setAudioName(e.target.value)}
                  placeholder="Original Audio"
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-colors"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(201,169,110,0.4)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                />
              </div>
            </div>

            {/* Publish button */}
            <button
              onClick={handlePublish}
              disabled={uploading || (!videoPreview)}
              className="w-full py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all disabled:opacity-40"
              style={{
                background: uploading || !videoPreview
                  ? 'rgba(255,255,255,0.1)'
                  : 'linear-gradient(135deg,#c9a96e,#e8c98a)',
                color: uploading || !videoPreview ? 'rgba(255,255,255,0.4)' : '#000',
              }}
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[16px] animate-spin">progress_activity</span>
                  Publishing…
                </span>
              ) : (
                'Share Reel'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReelCreator;
